import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import { colors } from '../constants';
import EmptyState from './EmptyState';
import LoadingState from './LoadingState';

/**
 * Generic table for the inventory section.
 *
 * columns: [{ key, label, align, width, render?: (row) => node }]
 * rows: array of row objects
 * When `hasNextPage`/`fetchNextPage` are provided, a "Load more" footer is shown.
 */
export default function DataTable({
  columns,
  rows = [],
  loading = false,
  getRowKey = (row, i) => row.uuid || row.id || i,
  onRowClick,
  emptyTitle,
  emptyMessage,
  emptyIcon,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}) {
  if (loading && rows.length === 0) {
    return <LoadingState />;
  }

  if (!loading && rows.length === 0) {
    return (
      <Paper elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 3 }}>
        <EmptyState icon={emptyIcon} title={emptyTitle} message={emptyMessage} />
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 3, overflow: 'hidden' }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: colors.gray100 }}>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || 'left'}
                  sx={{ fontWeight: 700, color: colors.gray700, width: col.width }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow
                key={getRowKey(row, i)}
                hover={!!onRowClick}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {columns.map((col) => (
                  <TableCell key={col.key} align={col.align || 'left'}>
                    {col.render ? col.render(row) : row[col.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {hasNextPage && (
        <Box sx={{ p: 2, textAlign: 'center', borderTop: `1px solid ${colors.border}` }}>
          <Button
            onClick={() => fetchNextPage && fetchNextPage()}
            disabled={isFetchingNextPage}
            sx={{ textTransform: 'none' }}
          >
            {isFetchingNextPage ? (
              <CircularProgress size={18} sx={{ color: colors.primary }} />
            ) : (
              'Load more'
            )}
          </Button>
        </Box>
      )}
    </Paper>
  );
}

/** Flattens an infinite query result ({ pages: [{ data: [] }] }) into a row array. */
export function flattenPages(query) {
  return (query?.data?.pages || []).flatMap((page) => page.data || []);
}
